import React, { useState, useEffect, useRef } from "react";

/**
 * ScreeningPDFPanel
 * - Displays PDF using presigned URL from backend
 * - Matches dimensions/style of ScreeningAbstractPanel
 */
const ScreeningPDFPanel = ({ literature }) => {
    const [pdfError, setPdfError] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 2;

    const pdfUrlRef = useRef(null);

    const handleRetry = () => {
        if (retryCount < MAX_RETRIES) {
            setRetryCount(prev => prev + 1);
            setPdfError(false);
            setPdfUrl(null);
        }
    };

    useEffect(() => {
        setPdfError(false);

        if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
        }
        setPdfUrl(null);

        const abortController = new AbortController();

        if (literature?.presigned_url) {
            setLoading(true);
            fetch(literature.presigned_url, { signal: abortController.signal })
                .then((response) => {
                    if (!response.ok) throw new Error("Failed to fetch PDF: " + response.status);
                    return response.blob();
                })
                .then((blob) => {
                    const url = URL.createObjectURL(
                        new Blob([blob], { type: "application/pdf" })
                    );
                    pdfUrlRef.current = url;
                    setPdfUrl(url);
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') {
                        console.error("Failed to load PDF:", error);
                        setPdfError(true);
                    }
                })
                .finally(() => setLoading(false));
        }

        return () => {
            abortController.abort();
            if (pdfUrlRef.current) {
                URL.revokeObjectURL(pdfUrlRef.current);
                pdfUrlRef.current = null;
            }
        };
    }, [literature?.presigned_url]);

    if (!literature) {
        return (
            <div className="ev-card-2 p-3 vh-65 d-flex align-items-center justify-content-center">
                <p className="ev-small-p-dark">No document available</p>
            </div>
        );
    }

    if (!literature.presigned_url && !pdfUrl) {
        return (
            <div className="ev-card-2 p-3 vh-65 d-flex flex-column justify-content-center align-items-center">
                <i className="bi bi-file-earmark-pdf ev-fs-48 text-muted mb-2"></i>
                <p className="ev-small-p-dark">PDF not available</p>
            </div>
        );
    }

    return (
        <div className="ev-card-2 p-3 vh-75 d-flex flex-column text-justify scrollable-element">
            {/* Header: Title and Author */}
            <div className="mb-3">
                <p className="ev-bold-reg ev-med-p-blue mb-1">
                    {literature.title || "Untitled Document"}
                </p>

                {/* Bibliographic Paper badge */}
                {literature.manually_added && (
                    <span className="ev-message-card3 px-2 py-1 ev-radius-xsmall ev-bold-med ev-fs-11 d-inline-block mt-1 mb-1">
                        <i className="bi bi-journal-text pe-1" />
                        Bibliographic Paper
                    </span>
                )}

                {literature.authors && literature.authors.length > 0 && (
                    <p className="ev-xsmall-p-dark mb-0">
                        Authors: {literature.authors.join(", ")}
                    </p>
                )}

                {literature.year && (
                    <p className="ev-xsmall-p-dark mb-0">
                        Year: {literature.year}
                    </p>
                )}
            </div>

            {/* PDF Viewer */}
            <div className="flex-grow-1 position-relative">
                {pdfError ? (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <i className="bi bi-exclamation-triangle ev-fs-48 text-warning mb-2"></i>
                        <p className="ev-small-p-dark mb-2">Failed to load PDF</p>
                        <div className="d-flex gap-2">
                            {retryCount < MAX_RETRIES && (
                                <button className="ev-btn-blue-fill-round ev-btn-small" onClick={handleRetry}>
                                    Retry
                                </button>
                            )}
                            <button
                                className="ev-btn-blue-fill-round ev-btn-small"
                                onClick={() => window.open(literature.presigned_url, "_blank")}
                            >
                                Open in New Tab
                            </button>
                        </div>
                    </div>
                ) : loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading PDF...</span>
                        </div>
                    </div>
                ) : pdfUrl ? (
                    <iframe
                        src={pdfUrl}
                        className="w-100 h-100 ev-radius-small"
                        style={{
                            border: "1px solid #e2e8f0",
                            minHeight: "500px"
                        }}
                        title="PDF Viewer"
                    ></iframe>
                ) : null}
            </div>
        </div>
    );
};

export default ScreeningPDFPanel;